import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { useLingui } from "@lingui/react/macro";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AppDialog } from "@/components/common/AppDialog";
import { useAuth } from "@/hooks/useAuth";
import {
  maintenanceApi,
  TENANT_DATA_PURGE_PHRASE,
  type TenantPurgeResponse,
  type TenantPurgeSummary,
} from "@/lib/api/services/maintenance.api";
import { getApiErrorMessage } from "@/lib/api/api";

/**
 * Support → Cleanup → Purge tenant data.
 *
 * Deletes every organization and everything ever created under one, while
 * users, global roles, permissions, seeded configuration and the audit trail
 * survive. The API is the authority on that boundary; this card's job is to
 * make sure nobody arrives there by accident:
 *
 *   preview first (counts, no writes) → a dialog that restates the blast
 *   radius → the exact confirmation phrase, retyped → then the request.
 *
 * The phrase the user types is the phrase the API demands verbatim, imported
 * from the same constant — the dialog cannot drift out of sync with the check.
 */

const SUMMARY_LABELS: Array<{ key: keyof TenantPurgeSummary; label: MessageDescriptor }> = [
  { key: "organizations", label: msg`Organizations` },
  { key: "companies", label: msg`Concepts` },
  { key: "regions", label: msg`Regions` },
  { key: "locations", label: msg`Locations` },
  { key: "recipes", label: msg`Recipes` },
  { key: "menus", label: msg`Menus` },
  { key: "materials", label: msg`Ingredients` },
  { key: "suppliers", label: msg`Suppliers` },
  { key: "cookingSessions", label: msg`Cooking sessions` },
  { key: "orders", label: msg`Orders` },
  { key: "scopedRoleAssignments", label: msg`Scoped role assignments` },
  { key: "organizationRoles", label: msg`Organization roles` },
];

function SummaryGrid({ summary }: { summary: TenantPurgeSummary }) {
  const { t } = useLingui();
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 sm:grid-cols-3">
      {SUMMARY_LABELS.map(({ key, label }) => (
        <div key={key} className="flex items-baseline justify-between gap-2 text-sm">
          <span className="text-(--color-text-secondary)">{t(label)}</span>
          <span className="font-medium tabular-nums">{summary[key]}</span>
        </div>
      ))}
    </div>
  );
}

export function TenantPurgeCard() {
  const { t } = useLingui();
  const { hasRole } = useAuth();
  const queryClient = useQueryClient();

  const [preview, setPreview] = useState<TenantPurgeResponse | null>(null);
  const [result, setResult] = useState<TenantPurgeResponse | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [phrase, setPhrase] = useState("");

  // The API gates both endpoints on the wildcard permission, which SUPPORT
  // does not hold — so the card says so instead of offering buttons that 403.
  const canPurge = hasRole("SUPER_ADMIN");

  const previewMutation = useMutation({
    mutationFn: maintenanceApi.previewTenantPurge,
    onSuccess: (response) => setPreview(response),
    onError: (error) => toast.error(getApiErrorMessage(error, t`Failed to load preview`)),
  });

  const applyMutation = useMutation({
    mutationFn: () =>
      maintenanceApi.applyTenantPurge({ confirm: true, confirmationPhrase: phrase }),
    onSuccess: (response) => {
      setResult(response);
      setPreview(null);
      setConfirmOpen(false);
      setPhrase("");
      // Every cached list on this session now describes deleted rows.
      queryClient.clear();
      toast.success(t`Tenant data purged`);
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t`Purge failed — nothing may have been deleted`)),
  });

  const openConfirm = () => {
    setPhrase("");
    setConfirmOpen(true);
    // The dialog shows what is about to be deleted; fetch the counts if the
    // operator skipped the preview button.
    if (!preview && !previewMutation.isPending) previewMutation.mutate();
  };

  const phraseMatches = phrase === TENANT_DATA_PURGE_PHRASE;

  return (
    <Card className="border-(--color-danger)/40">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="size-5 text-(--color-danger)" />
          {t`Purge tenant data`}
        </CardTitle>
        <CardDescription>
          {t`Deletes every organization and everything created under one: concepts, regions, locations, recipes, menus, ingredients, suppliers, inventory, orders, and cooking sessions. Users, global roles, permissions, seeded configuration, and audit logs are kept. This cannot be undone.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!canPurge ? (
          <p className="text-sm text-(--color-text-secondary)">
            {t`Only an administrator with full access can run this purge.`}
          </p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                disabled={previewMutation.isPending}
                onClick={() => previewMutation.mutate()}
              >
                {previewMutation.isPending && <Loader2 className="size-4 animate-spin" />}
                {t`Preview`}
              </Button>
              <Button variant="destructive" onClick={openConfirm} className="gap-2">
                <Trash2 className="size-4" />
                {t`Purge all tenant data…`}
              </Button>
            </div>

            {preview && (
              <div className="space-y-3 rounded-md border border-(--color-border) p-4">
                <p className="text-sm font-medium">{t`This purge would delete:`}</p>
                <SummaryGrid summary={preview.summary} />
                <p className="text-xs text-(--color-text-secondary)">{preview.details.note}</p>
              </div>
            )}

            {result && (
              <div className="space-y-3 rounded-md border border-(--color-border) bg-(--color-surface-muted)/50 p-4">
                <p className="text-sm font-medium">{t`Purge complete. Deleted:`}</p>
                <SummaryGrid summary={result.summary} />
                <ul className="list-disc pl-5 text-xs text-(--color-text-secondary)">
                  {result.details.kept.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>

      <AppDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!applyMutation.isPending) setConfirmOpen(open);
        }}
        title={t`Purge all tenant data?`}
        description={t`Every organization and everything created under one will be permanently deleted. Users, global roles, permissions, and configuration are kept.`}
        footer={
          <div className="flex w-full items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={applyMutation.isPending}
            >
              {t`Cancel`}
            </Button>
            <Button
              variant="destructive"
              disabled={!phraseMatches || applyMutation.isPending}
              onClick={() => applyMutation.mutate()}
              className="gap-2"
            >
              {applyMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Trash2 className="size-4" />
              )}
              {t`Purge everything`}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {preview ? (
            <div className="rounded-md border border-(--color-border) p-3">
              <SummaryGrid summary={preview.summary} />
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-(--color-text-secondary)">
              <Loader2 className="size-4 animate-spin" />
              {t`Counting what would be deleted…`}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="purge-confirm-phrase">
              {t`Type ${TENANT_DATA_PURGE_PHRASE} to confirm`}
            </Label>
            <Input
              id="purge-confirm-phrase"
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              placeholder={TENANT_DATA_PURGE_PHRASE}
              autoComplete="off"
              disabled={applyMutation.isPending}
            />
          </div>
        </div>
      </AppDialog>
    </Card>
  );
}
